import { importPage, PRODUCT_NAME } from "@staffsmith/web-auth";
import { useQuery } from "@tanstack/react-query";
import { Link, Navigate, Outlet, type RouteObject, useNavigate } from "react-router";
import { ApiError } from "./api.js";
import { useServices } from "./context.js";
import { SignInPage } from "./pages/SignIn.js";
import { ErrorNote, Loading } from "./ui.js";

// The operator app: the tenant list and one tenant's page, and model governance. Each page loads only
// when first visited (ADR 0003: route-level code splitting).
const page = (load: () => Promise<{ default: React.ComponentType }>) => async () => ({
  Component: (await importPage(load))?.default ?? (() => null),
});

function Layout() {
  const { api, auth } = useServices();
  const navigate = useNavigate();
  const me = useQuery({ queryKey: ["me"], queryFn: api.me, enabled: auth.isSignedIn() });
  if (!auth.isSignedIn()) return <Navigate to="/sign-in" replace />;
  const noAccess = me.error instanceof ApiError && me.error.status === 403;
  return (
    <div className="shell">
      <nav className="nav" aria-label="Main">
        <Link className="brand" to="/">
          {PRODUCT_NAME} operators
        </Link>
        <Link to="/models">Models</Link>
        <Link to="/health">Health</Link>
        <Link to="/incidents">Incidents</Link>
        <span className="spacer" />
        <span className="muted">{me.data?.display_name ?? ""}</span>
        <Link to="/passkeys">Passkeys</Link>
        <button
          type="button"
          className="link"
          onClick={() => {
            auth.signOut();
            void navigate("/sign-in");
          }}
        >
          Sign out
        </button>
      </nav>
      <main>
        {me.isPending ? <Loading /> : null}
        {noAccess ? (
          <p className="notice">This sign-in isn't an active operator.</p>
        ) : (
          <ErrorNote error={me.error} />
        )}
        {me.data ? <Outlet /> : null}
      </main>
    </div>
  );
}

export const routes: RouteObject[] = [
  { path: "/sign-in", element: <SignInPage /> },
  {
    path: "/",
    element: <Layout />,
    children: [
      { index: true, lazy: page(() => import("./pages/Tenants.js")) },
      { path: "tenants/:tenantId", lazy: page(() => import("./pages/Tenant.js")) },
      { path: "passkeys", lazy: page(() => import("./pages/Passkeys.js")) },
      { path: "models", lazy: page(() => import("./pages/Models.js")) },
      { path: "health", lazy: page(() => import("./pages/Health.js")) },
      { path: "incidents", lazy: page(() => import("./pages/Incidents.js")) },
      { path: "incidents/:incidentId", lazy: page(() => import("./pages/Incident.js")) },
      { path: "models/:agent", lazy: page(() => import("./pages/AgentModel.js")) },
      { path: "eval-runs/:runId", lazy: page(() => import("./pages/EvalRun.js")) },
    ],
  },
];
